import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck, Search, RotateCcw, SlidersHorizontal, Filter, Eye, Trash2, Wrench } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  DataTable,
  StatusPill,
  EmptyState,
  Pagination,
  FilterBuilder,
  type ColumnDef,
  type FilterField,
  type FilterCondition,
} from '@vnetwork/ui/data';
import {
  Input,
  Badge,
  Skeleton,
  Button,
  IconButton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Tooltip,
  TooltipProvider,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useWafSslCerts,
  useWafSslOptions,
  useDeleteWafSslCert,
  fmtEpoch,
  wafExtractError,
  type WafSslCert,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { useDebounce } from '../../shared';
import { cap, sslVendorTypeLabel } from '../domains/format';
import { SslVerifyDialog } from './SslVerifyDialog';
import styles from './WafSslList.module.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

// The SSL search fields (cn | domain | fqdn) are all domains, so the keyword must
// be a valid hostname — a wildcard prefix (*.example.com) is allowed for certs.
const DOMAIN_RE = /^(\*\.)?([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

// Filterable fields → backend query params. Single-value (the BE reads one value
// per field, unlike the domain list's CSV multi-select).
const FILTER_FIELD_IDS = ['type', 'status', 'expires_after', 'expires_before'] as const;
type FilterFieldId = (typeof FILTER_FIELD_IDS)[number];

const TOGGLEABLE = [
  { id: 'type', label: 'Type' },
  { id: 'status', label: 'Status' },
  { id: 'san', label: 'SAN' },
  { id: 'issuer', label: 'Issuer' },
  { id: 'expiry', label: 'Expiry' },
  { id: 'created', label: 'Created' },
];
const DEFAULT_VISIBLE: Record<string, boolean> = {
  type: true, status: true, san: false, issuer: false, expiry: true, created: true,
};

type Tone = 'success' | 'danger' | 'warning' | 'muted';

/** The vendor's "normal" binding state means the cert is active/in-use. */
const normalizeStatus = (s?: string) => {
  const v = (s ?? '').toLowerCase();
  return v === 'normal' ? 'active' : v;
};

/** Map an SSL cert status/binding/state token to a StatusPill tone. */
function sslStatusTone(s?: string): Tone {
  const v = (s ?? '').toLowerCase();
  if (v === 'active' || v === 'issued') return 'success';
  if (v === 'issuing' || v === 'waiting' || v === 'pending') return 'warning';
  if (v === 'deleting' || v === 'inactive' || v === 'failed' || v === 'error') return 'danger';
  return 'muted';
}

const muted = (v: React.ReactNode) => <span style={{ color: 'var(--vn-fg-muted)' }}>{v}</span>;

/** Chips with a `+N` overflow (used for bindings / SAN). */
function chipList(values: string[] | undefined, max = 3) {
  const items = values ?? [];
  if (items.length === 0) return muted('—');
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
      {items.slice(0, max).map((v) => (
        <Badge key={v} tone="neutral" appearance="subtle" size="sm">{v}</Badge>
      ))}
      {items.length > max ? muted(`+${items.length - max}`) : null}
    </span>
  );
}

/** Unix epoch (seconds or millis) → `DD/MM/YYYY`; "—" when absent. */
function fmtDate(v?: number) {
  if (!v) return '—';
  const ms = v < 1e12 ? v * 1000 : v;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

/** Expiry warning badge derived from `not_after` (unix s or ms). */
function expiryBadge(notAfter?: number) {
  if (!notAfter) return null;
  const ms = notAfter < 1e12 ? notAfter * 1000 : notAfter;
  const days = (ms - Date.now()) / 86_400_000;
  if (days < 0) return <Badge tone="danger" appearance="subtle" size="sm">Expired</Badge>;
  if (days < 30) return <Badge tone="warning" appearance="subtle" size="sm">Expiring soon</Badge>;
  return null;
}

/** A `yyyy-mm-dd` date string → unix seconds (undefined when empty/invalid). */
const dateToUnix = (d?: string) => {
  if (!d) return undefined;
  const t = Date.parse(d);
  return Number.isNaN(t) ? undefined : Math.floor(t / 1000);
};

/** Row actions: view assigned domains (active certs) + delete (deletable certs). */
function SslActionsCell({ serviceId, cert }: { serviceId: string; cert: WafSslCert }) {
  const { show } = useToast();
  const [domainsOpen, setDomainsOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useDeleteWafSslCert(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Certificate deleted' }); trackFeatureAction('waf', 'ssl_cert', 'delete'); setConfirmDelete(false); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Delete failed') }),
  });
  const isActive = normalizeStatus(cert.status || cert.binding_state || cert.state) === 'active';

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', gap: 2 }}>
      <TooltipProvider>
        {cert.chip === 'setup' && (
          <Tooltip content="Set up verification" side="top">
            <IconButton
              variant="ghost"
              size="sm"
              aria-label="Set up verification"
              onClick={() => setVerifyOpen(true)}
            >
              <Wrench size={14} />
            </IconButton>
          </Tooltip>
        )}
        {isActive && (
          <Popover open={domainsOpen} onOpenChange={setDomainsOpen}>
            <Tooltip content="View assigned domains" side="top">
              <PopoverTrigger asChild>
                <IconButton variant="ghost" size="sm" aria-label="View assigned domains">
                  <Eye size={14} />
                </IconButton>
              </PopoverTrigger>
            </Tooltip>
            <PopoverContent align="end" style={{ padding: 'var(--vn-space-3)', maxWidth: 280 }}>
              <div style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 500, marginBottom: 'var(--vn-space-2)' }}>
                Assigned domains
              </div>
              {cert.bindings?.length ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {cert.bindings.map((d) => (
                    <Badge key={d} tone="info" appearance="subtle" size="sm">{d}</Badge>
                  ))}
                </div>
              ) : (
                <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                  No assigned domains.
                </span>
              )}
            </PopoverContent>
          </Popover>
        )}
        {cert.deletable && (
          <Tooltip content="Delete" side="top">
            <IconButton
              variant="ghost"
              size="sm"
              aria-label="Delete certificate"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 size={14} />
            </IconButton>
          </Tooltip>
        )}
      </TooltipProvider>

      {confirmDelete && (
        <ConfirmDialog
          open
          onOpenChange={(o) => { if (!o) setConfirmDelete(false); }}
          title="Delete certificate?"
          description={`Delete the certificate for "${cert.cn}"? This cannot be undone.`}
          confirmLabel="Delete"
          destructive
          loading={del.isPending}
          onConfirm={() => del.mutate({ id: cert.id, type: cert.type })}
        />
      )}

      {verifyOpen && (
        <SslVerifyDialog
          serviceId={serviceId}
          certId={cert.id}
          cn={cert.cn}
          open={verifyOpen}
          onOpenChange={setVerifyOpen}
        />
      )}
    </div>
  );
}

export function WafSslList({ serviceId }: { serviceId: string }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(() => Math.max(1, Number(searchParams.get('page') || '1')));
  const [pageSize, setPageSize] = useState(() => {
    const p = Number(searchParams.get('page_size') || String(DEFAULT_PAGE_SIZE));
    return PAGE_SIZE_OPTIONS.includes(p) ? p : DEFAULT_PAGE_SIZE;
  });
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const debouncedSearch = useDebounce(searchInput, 400);
  const [searchBy, setSearchBy] = useState(() => searchParams.get('search_by') || 'cn');
  const [visible, setVisible] = useState<Record<string, boolean>>(DEFAULT_VISIBLE);
  // "Needs setup" is a standalone quick-filter button, not a FilterBuilder field.
  const [setupOnly, setSetupOnly] = useState(() => searchParams.get('chip') === 'setup');

  const [conditions, setConditions] = useState<FilterCondition[]>(() => {
    const seed: FilterCondition[] = [];
    for (const id of FILTER_FIELD_IDS) {
      const v = searchParams.get(id);
      if (v) seed.push({ id: `init-${id}`, field: id, operator: 'is', value: v });
    }
    return seed;
  });
  const [filterOpen, setFilterOpen] = useState(false);

  // Filter catalog. Wait for it so the filter UI never renders half-populated.
  const { data: options, isLoading: optionsLoading } = useWafSslOptions(serviceId);
  const filterFields: FilterField[] = useMemo(
    () => [
      { value: 'type', label: 'Type', input: 'select', operators: [{ value: 'is', label: 'is' }], options: options?.types ?? [] },
      { value: 'status', label: 'Status', input: 'select', operators: [{ value: 'is', label: 'is' }], options: options?.statuses ?? [] },
      { value: 'expires_after', label: 'Expires after', input: 'date', operators: [{ value: 'is', label: 'on or after' }] },
      { value: 'expires_before', label: 'Expires before', input: 'date', operators: [{ value: 'is', label: 'on or before' }] },
    ],
    [options],
  );
  // "fqdn" is effectively the same as "domain" — hide it from the search-by dropdown.
  const searchFields = (options?.search_fields ?? [{ value: 'cn', label: 'Common Name' }]).filter(
    (f) => f.value !== 'fqdn',
  );

  const filterValue = (field: FilterFieldId) =>
    conditions.find((c) => c.field === field && c.value.trim())?.value.trim() || undefined;
  const typeFilter = filterValue('type');
  const statusFilter = filterValue('status');
  const chipFilter = setupOnly ? 'setup' : undefined;
  const expiresAfterRaw = filterValue('expires_after');
  const expiresBeforeRaw = filterValue('expires_before');
  const hasFilters = conditions.some((c) => c.value.trim());
  const setupLabel = options?.chips?.[0]?.label ?? 'Needs setup';

  // The keyword must be a valid domain. Show the error immediately (on the raw
  // input) but only gate the actual query on the debounced value.
  const searchError =
    searchInput.trim() && !DOMAIN_RE.test(searchInput.trim())
      ? 'Enter a valid domain, e.g. example.com or *.example.com'
      : '';
  const debTrimmed = debouncedSearch.trim();
  const activeSearch = debTrimmed && DOMAIN_RE.test(debTrimmed) ? debTrimmed : undefined;

  // Keep page/page_size/search/search_by + active filters in the URL.
  useEffect(() => {
    const params: Record<string, string> = { page: String(page), page_size: String(pageSize) };
    if (activeSearch) {
      params.search = activeSearch;
      params.search_by = searchBy;
    }
    if (typeFilter) params.type = typeFilter;
    if (statusFilter) params.status = statusFilter;
    if (chipFilter) params.chip = chipFilter;
    if (expiresAfterRaw) params.expires_after = expiresAfterRaw;
    if (expiresBeforeRaw) params.expires_before = expiresBeforeRaw;
    setSearchParams(params, { replace: true });
  }, [
    page, pageSize, activeSearch, searchBy, typeFilter, statusFilter, chipFilter,
    expiresAfterRaw, expiresBeforeRaw, setSearchParams,
  ]);

  const { data, isLoading, isError, isFetching, refetch } = useWafSslCerts(serviceId, {
    page,
    page_size: pageSize,
    search: activeSearch,
    search_by: activeSearch ? searchBy : undefined,
    type: typeFilter,
    status: statusFilter,
    chip: chipFilter,
    expires_after: dateToUnix(expiresAfterRaw),
    expires_before: dateToUnix(expiresBeforeRaw),
  });

  const rows = data?.items ?? [];
  const total = data?.pagination?.total ?? 0;
  const pageCount = Math.max(1, data?.pagination?.total_pages ?? 1);

  const columns = useMemo<ColumnDef<WafSslCert, unknown>[]>(() => {
    const all: Record<string, ColumnDef<WafSslCert, unknown>> = {
      type: {
        id: 'type',
        header: 'Type',
        enableSorting: false,
        cell: ({ row }) => sslVendorTypeLabel(row.original.type),
      },
      status: {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const st = normalizeStatus(row.original.status || row.original.binding_state || row.original.state);
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <StatusPill tone={sslStatusTone(st)}>{st ? cap(st) : 'Unknown'}</StatusPill>
              {row.original.chip === 'setup' && (
                <Badge tone="warning" appearance="subtle" size="sm">Needs setup</Badge>
              )}
            </span>
          );
        },
      },
      san: {
        id: 'san',
        header: 'SAN',
        enableSorting: false,
        cell: ({ row }) => chipList(row.original.san),
      },
      issuer: {
        id: 'issuer',
        header: 'Issuer',
        enableSorting: false,
        cell: ({ row }) => (row.original.issuer ? muted(row.original.issuer) : muted('—')),
      },
      expiry: {
        id: 'expiry',
        header: 'Expiry',
        enableSorting: false,
        cell: ({ row }) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {fmtDate(row.original.not_after)}
            {expiryBadge(row.original.not_after)}
          </span>
        ),
      },
      created: {
        id: 'created',
        header: 'Created',
        enableSorting: false,
        cell: ({ row }) => muted(fmtEpoch(row.original.created_at)),
      },
    };
    const cnCol: ColumnDef<WafSslCert, unknown> = {
      id: 'cn',
      accessorKey: 'cn',
      header: 'Common Name',
      cell: ({ row }) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 500 }}>
          <ShieldCheck size={14} color="var(--vn-fg-muted)" />
          {row.original.cn || muted('—')}
        </span>
      ),
    };
    const actionsCol: ColumnDef<WafSslCert, unknown> = {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => <SslActionsCell serviceId={serviceId} cert={row.original} />,
    };
    return [cnCol, ...TOGGLEABLE.filter((c) => visible[c.id]).map((c) => all[c.id]), actionsCol];
  }, [visible, serviceId]);

  return (
    <Container size="xl">
      <PageHeader
        icon={<ShieldCheck size={22} />}
        title={`SSL Certificates${total ? ` (${total})` : ''}`}
        description="SSL/TLS certificates for this WAF service."
      />

      <div className={styles.toolbar}>
        <div className={styles.searchGroup}>
          <Input
            wrapperClassName={styles.search}
            placeholder="Search by domain, e.g. example.com"
            startSlot={<Search />}
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
            aria-invalid={!!searchError}
          />
          <Select value={searchBy} onValueChange={(v) => { setSearchBy(v); setPage(1); }}>
            <SelectTrigger aria-label="Search by" className={styles.searchBy}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {searchFields.map((f) => (
                <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className={styles.actions}>
          <Button
            variant={setupOnly ? 'primary' : 'secondary'}
            onClick={() => { setSetupOnly((v) => !v); setPage(1); }}
            aria-pressed={setupOnly}
          >
            {setupLabel}
          </Button>
          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="secondary" startIcon={<Filter size={15} />} disabled={optionsLoading}>
                Filters{hasFilters ? ` (${conditions.filter((c) => c.value.trim()).length})` : ''}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" style={{ padding: 0 }}>
              <FilterBuilder
                fields={filterFields}
                defaultValue={conditions.length ? conditions : undefined}
                onApply={(c) => {
                  setConditions(c.filter((x) => x.value.trim()));
                  setPage(1);
                  setFilterOpen(false);
                }}
                onClose={() => setFilterOpen(false)}
              />
            </PopoverContent>
          </Popover>
          {hasFilters && (
            <Button variant="ghost" onClick={() => { setConditions([]); setPage(1); }}>
              Clear
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                Display options
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
              {TOGGLEABLE.map((c) => (
                <DropdownMenuCheckboxItem
                  key={c.id}
                  checked={visible[c.id]}
                  onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [c.id]: v === true }))}
                  onSelect={(e) => e.preventDefault()}
                >
                  {c.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="secondary"
            startIcon={<RotateCcw size={15} />}
            onClick={() => refetch()}
            aria-label="Refresh certificates list"
          >
            Refresh
          </Button>
        </div>
      </div>

      {searchError && (
        <div style={{ color: 'var(--vn-danger-text)', fontSize: 'var(--vn-font-size-sm)', marginTop: 'calc(-1 * var(--vn-space-1))', marginBottom: 'var(--vn-space-3)' }}>
          {searchError}
        </div>
      )}

      {isLoading || optionsLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 48 }} />
          ))}
        </div>
      ) : isError ? (
        <Alert
          tone="danger"
          title="Couldn't load certificates"
          actions={<button onClick={() => refetch()}>Retry</button>}
        >
          The WAF service couldn't return its SSL certificates. Try again.
        </Alert>
      ) : total === 0 && !activeSearch && !hasFilters && !setupOnly ? (
        <EmptyState
          icon={<ShieldCheck size={20} />}
          title="No certificates yet"
          description="This service has no SSL certificates."
        />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(r) => String(r.id)}
            emptyState="No certificates match your search or filters."
            footer={`Showing ${rows.length ? (page - 1) * pageSize + 1 : 0}–${(page - 1) * pageSize + rows.length} of ${total}`}
          />
          {(pageCount > 1 || PAGE_SIZE_OPTIONS.length > 1) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--vn-space-4)',
                marginTop: 'var(--vn-space-4)',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                <label
                  htmlFor="waf-ssl-page-size"
                  style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}
                >
                  Rows per page
                </label>
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                  <SelectTrigger id="waf-ssl-page-size" size="sm" style={{ width: 72 }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {pageCount > 1 && (
                <Pagination
                  page={page}
                  pageCount={pageCount}
                  onPageChange={setPage}
                  summary={isFetching ? 'Loading…' : `Page ${page} of ${pageCount}`}
                />
              )}
            </div>
          )}
        </>
      )}
    </Container>
  );
}

export default WafSslList;
